import { useToast } from 'primevue/usetoast';
import { logger } from '@logging/logging';

const readingTimePerCharacter = 60; // ~1,5x het gemiddelde leestempo
const noticeTime = 1500; // tijd om de melding op te merken, inclusief de entry-animatie van 300ms
const minDuration = 3000;
const maxDuration = 10000;

/*
 * Berekent hoe lang een melding blijft staan. Meldingen mogen HTML bevatten (zie useErrorHandler),
 * dus we tellen alleen de zichtbare tekst: anders bepaalt de opmaak mede de leestijd.
 * Het plafond mag ruim zijn omdat de melding blijft staan zolang de muis erop staat (zie toast/hoverPause).
 */
export const notificationDuration = (message: string) => {
  const characters = message.replace(/<[^>]+>/g, ' ').trim().length;
  const calculatedDuration = noticeTime + characters * readingTimePerCharacter;
  return Math.max(minDuration, Math.min(calculatedDuration, maxDuration));
};

export const useNotification = () => {
  try {
    const toast = useToast();

    const success = (message: string, indefinite = false) =>
      toast.add({
        severity: 'success',
        detail: message,
        life: indefinite ? undefined : notificationDuration(message),
      });

    const error = (message: string) => {
      toast.add({ severity: 'error', detail: message });
    };

    const warning = (message: string, indefinite = false) => {
      toast.add({
        severity: 'warn',
        detail: message,
        life: indefinite ? undefined : notificationDuration(message),
      });
    };

    const info = (message: string, indefinite = false) =>
      toast.add({
        severity: 'info',
        detail: message,
        life: indefinite ? undefined : notificationDuration(message),
      });

    return {
      success,
      error,
      warning,
      info,
    };
  } catch (error) {
    // dit komt voor als de notification buiten een setup functie wordt gebruikt.
    logger.warn('Error initializing toast', error);
    return {
      success: (message: string) => console.error('Unsent success toast:', message),
      error: (message: string) => console.error('Unsent error toast:', message),
      warning: (message: string) => console.error('Unsent warning toast:', message),
      info: (message: string) => console.error('Unsent info toast:', message),
    };
  }
};
